import React, { Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { gql } from '@apollo/client'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { isMobile } from 'react-device-detect'
import { CaptionSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { Modal } from '@/molecules/Modal'
import { CompletionStep } from '@/organisms/ShareGuildTicketsModal/CompletionStep'
import { ConfirmStep } from '@/organisms/ShareGuildTicketsModal/ConfirmationStep'
import { DefaultStep } from '@/organisms/ShareGuildTicketsModal/DefaultStep'
import { ShareLinkStep } from '@/organisms/ShareGuildTicketsModal/ShareLinkStep'
import { createBasicShareLink } from '@/services/Sharing'
import { useUser } from '@/services/UserService'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'

const DONATE_GUILD_TICKETS_MUTATION = gql`
  mutation DiscountsDonateGuildToPif($input: DiscountsDonateGuildToPifInput!) {
    discountsDonateGuildToPif(input: $input) {
      quantityGifted
      error {
        description
        reference
      }
    }
  }
`

interface ShareGuildTicketsModalProps {
  open: boolean
  onClose: () => void
  projectName: string
  theatricalSlug: string
  modalRef?: Ref<HTMLDivElement>
  ModalStep?: ModalStep
  showtimesLink?: string
  source: 'tickets' | 'landing' | 'default'
}

type ModalStep = 'main' | 'gift-confirmation' | 'gift-completion' | 'gift-share-link'

export const ShareGuildTicketsModal = ({
  open,
  onClose,
  projectName,
  theatricalSlug,
  modalRef,
  ModalStep = 'main',
  showtimesLink,
  source = 'default',
}: ShareGuildTicketsModalProps) => {
  const { t } = useTranslate('common')
  const { isReady, query } = useSafeRouter()
  const { isLoggedIn } = useUser()
  const [isCopied, setIsCopied] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState<ModalStep>(ModalStep)
  const timeoutRef = useRef<NodeJS.Timeout>()
  const { discountCodes, giftGuildTicketsMobile, refetchGuildTickets } = useGuildTickets(theatricalSlug)

  const handleModalClose = useCallback(() => {
    if (currentStep === 'gift-completion') {
      refetchGuildTickets().catch((err) => {
        logger().error('Failed to refetch guild tickets', {
          err,
          theatricalSlug,
        })
      })
    }
    onClose()
  }, [currentStep, onClose, refetchGuildTickets, theatricalSlug])

  const urlTicketCodes = useMemo(() => {
    const queryDiscounts = getItemOrFirstEntry(query?.discounts)
    return queryDiscounts ? queryDiscounts.split(',').filter(Boolean) : []
  }, [query?.discounts])

  const effectiveDiscountCodes = useMemo(() => {
    return isLoggedIn ? discountCodes : urlTicketCodes
  }, [isLoggedIn, discountCodes, urlTicketCodes])

  const effectiveTicketCount = useMemo(() => {
    return effectiveDiscountCodes?.length ?? 0
  }, [effectiveDiscountCodes])

  useEffect(() => {
    if (open) {
      setCurrentStep(ModalStep)
    }
  }, [open, ModalStep])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        setIsCopied(false)
      }
    }
  }, [])

  useEffect(() => {
    if (!effectiveDiscountCodes || effectiveDiscountCodes.length === 0) {
      setShareUrl(null)
      return
    }

    createBasicShareLink({
      baseUrl: `${paths.getBaseURL()}/tickets/${theatricalSlug}`,
      params: { flow: 'guild-gift', discounts: effectiveDiscountCodes.join(',') },
    }).then((url: string) => setShareUrl(url))
  }, [effectiveDiscountCodes, theatricalSlug])

  const onCopyClick = useCallback(async () => {
    if (!shareUrl) return

    if (isMobile && typeof navigator?.share === 'function') {
      await giftGuildTicketsMobile()
      return
    }
    if (navigator?.clipboard && source === 'tickets') {
      setCurrentStep('gift-share-link')
    }
    if (navigator?.clipboard && source !== 'tickets') {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setIsCopied(true)
      })

      const timeout = setTimeout(() => {
        setIsCopied(false)
      }, 2000)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = timeout
    }
  }, [shareUrl, source, giftGuildTicketsMobile])

  const handleGiftClick = useCallback(() => {
    setCurrentStep('gift-confirmation')
  }, [])

  const handleGoBack = useCallback(() => {
    setCurrentStep('main')
  }, [])

  const [donateGuildTickets, { loading: isDonating }] = useMutation(DONATE_GUILD_TICKETS_MUTATION)

  const handleGiftConfirm = useCallback(async () => {
    if (!effectiveDiscountCodes || effectiveDiscountCodes.length === 0) return

    try {
      const input = {
        quantity: effectiveDiscountCodes.length,
        codes: effectiveDiscountCodes,
        theatricalSlug,
      }

      const result = await donateGuildTickets({
        variables: {
          input: input,
        },
      })

      const { error } = result.data?.discountsDonateGuildToPif || {}

      if (error) {
        logger().error('Donating Guild Tickets Failed', {
          error,
          theatricalSlug,
        })
        return
      }

      setCurrentStep('gift-completion')
    } catch (err) {
      logger().error('Donating Guild Tickets Failed', {
        err,
        theatricalSlug,
      })
    }
  }, [effectiveDiscountCodes, donateGuildTickets, theatricalSlug])

  const renderStepContent = () => {
    const commonProps = {
      onClose: handleModalClose,
      ticketQuantity: effectiveTicketCount,
      projectName,
    }
    switch (currentStep) {
      case 'gift-confirmation':
        return (
          <ConfirmStep
            {...commonProps}
            handleGiftConfirm={handleGiftConfirm}
            handleGoBack={handleGoBack}
            isLoading={isDonating}
            theatricalSlug={theatricalSlug}
          />
        )
      case 'gift-share-link':
        return shareUrl ? (
          <ShareLinkStep
            {...commonProps}
            handleGiftConfirm={handleGiftConfirm}
            handleGoBack={handleGoBack}
            isLoading={isDonating}
            shareUrl={shareUrl}
          />
        ) : null
      case 'gift-completion':
        return <CompletionStep {...commonProps} />
      default:
        return (
          <DefaultStep
            onClose={onClose}
            onCopyClick={onCopyClick}
            handleGiftClick={handleGiftClick}
            showtimesLink={showtimesLink || ''}
            source={source}
            discountCodes={effectiveDiscountCodes}
          />
        )
    }
  }

  if (!isReady) {
    return null
  }

  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={handleModalClose}
      backgroundOverlayClassName="!bg-black/85"
      panelClasses={classNames(
        'relative shadow-dark-5 rounded-3xl max-w-[calc(100vw-24px)]   md:max-w-[500] !p-0 relative text-center',
        source === 'tickets' ? 'md:!w-[480px]' : 'md:!w-[500px]',
      )}
      transition="slide"
      className="z-[99999]"
    >
      <div
        ref={modalRef}
        className={classNames(
          'h-full w-full min-w-[19.5em] !p-6 !pt-8 md:!p-10 md:!pt-12',
          source === 'tickets' ? 'md:!w-[480px]' : 'md:!w-[500px]',
        )}
      >
        {renderStepContent()}
        <AnimatePresence>
          {isCopied && (
            <motion.div
              className="absolute inset-x-0 bottom-3.5"
              transition={{
                y: { type: 'spring', bounce: 0.3 },
                opacity: { duration: 0.3 },
                ease: 'easeInOut',
              }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
            >
              <CaptionSM color="core-gray-700">{t('urlCopiedToClipBoard', 'URL copied to clipboard')}</CaptionSM>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Modal>
  )
}
